"use client";

import Link from "@/lib/router/link";
import { usePathname } from "@/lib/router/navigation";
import { ShieldHalf } from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
  SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail,
} from "@/components/ui/sidebar";
import { LogoMark } from "@/components/brand/logo";
import { platformNavItems } from "@/config/nav";
import { cn } from "@/lib/utils";
import { branding } from "@/config/branding";

export function PlatformSidebar() {
  const pathname = usePathname();
  const isActive = (href: string, exact?: boolean) =>
    exact ? pathname === href : pathname === href || pathname.startsWith(href + "/");

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="gap-2.5 px-3 pb-1 pt-4">
        <Link href="/platform" className="flex items-center gap-2.5 group-data-[collapsible=icon]:justify-center">
          <LogoMark />
          <span className="flex flex-col leading-none group-data-[collapsible=icon]:hidden">
            <span className="text-[15px] font-bold tracking-tight">{branding.name}</span>
            <span className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Platform Admin</span>
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-2.5">
        <SidebarGroup className="py-1">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {platformNavItems.map((item) => {
                const active = isActive(item.href, item.exact);
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={active} tooltip={item.title}
                      className={cn("h-9 gap-2.5 rounded-md text-[13px] font-medium transition-colors",
                        active ? "bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary" : "text-foreground/70 hover:bg-accent hover:text-foreground")}>
                      <Link href={item.href}>
                        <item.icon className={cn("size-[18px]", active && "text-primary")} />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="gap-2 px-3 pb-3 group-data-[collapsible=icon]:hidden">
        {/* 3.143 (the sponsor, 2026-10-11): no "Exit to console". An operator with no organisation has no console to
            go to, and a tenant page would only show requests that fail. */}
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2.5 py-2">
          <ShieldHalf className="size-3.5 shrink-0 text-primary" />
          <span className="text-xs text-muted-foreground">Platform operator</span>
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
